import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useLanguage, useTheme, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, Sun, Moon, Mail, ArrowRight, Loader2, Lock,
  Shield, Sparkles, Calendar, Eye, EyeOff,
  TrendingUp, MessageSquare, Bell,
  AlertTriangle, CreditCard, MessageCircle
} from "lucide-react";

const HEADLINES = [
  { it: "Trasforma ogni richiesta in un appuntamento", en: "Turn every request into a booking" },
  { it: "Automatizza e fai crescere il tuo business", en: "Automate and grow your business" },
  { it: "Meno no-show, più clienti, più fatturato", en: "Fewer no-shows, more clients, more revenue" },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser, login: googleLogin } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [suspendedInfo, setSuspendedInfo] = useState(null);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    const interval = setInterval(() => setHeadlineIdx(p => (p + 1) % HEADLINES.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email.includes("@") || !password || loading) return;
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
        remember_me: rememberMe,
      }, { withCredentials: true });

      if (res.data.user) {
        setUser(res.data.user);
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Suspended-account flow: backend returns 403 with structured detail
      if (
        err.response?.status === 403 &&
        detail &&
        typeof detail === "object" &&
        detail.code === "ACCOUNT_SUSPENDED"
      ) {
        setSuspendedInfo(detail);
      } else if (typeof detail === "string" && detail.includes("Imposta una password")) {
        setError(language === "it" ? "Devi impostare una password. Clicca 'Password dimenticata' per crearne una." : "You need to set a password. Click 'Forgot password' to create one.");
      } else {
        setError(language === "it" ? "Email o password non corretti" : "Incorrect email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const headline = HEADLINES[headlineIdx];

  return (
    <div className="min-h-screen flex bg-white dark:bg-slate-950">
      {/* LEFT — AUTH */}
      <div className={`flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-8 transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="w-full max-w-[400px] mx-auto">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-10">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <div className="flex items-center gap-1">
              <button onClick={() => changeLanguage(language === "it" ? "en" : "it")} className="px-2 py-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-xs font-medium text-slate-400">
                {language === "it" ? "IT/ENG" : "ENG/IT"}
              </button>
              <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                {theme === "light" ? <Moon className="w-4 h-4 text-slate-400" /> : <Sun className="w-4 h-4 text-slate-400" />}
              </button>
            </div>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/smartbooker-icon-48.png" alt="SmartBooker" className="h-9 w-9" />
            <span className="font-semibold text-lg text-slate-900 dark:text-white">SmartBooker</span>
          </div>

          {/* Heading */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white leading-tight">
              {language === "it" ? "Accedi al tuo account" : "Sign in to your account"}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
              {language === "it" ? "Inserisci le tue credenziali" : "Enter your credentials"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  placeholder={language === "it" ? "La tua email" : "Your email"}
                  className="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  data-testid="login-email-input"
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
                <Link to="/forgot-password" className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium transition-colors" data-testid="forgot-password-link">
                  {language === "it" ? "Password dimenticata?" : "Forgot password?"}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  placeholder="********"
                  className="w-full h-12 pl-10 pr-12 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-500 text-sm" data-testid="login-error">{error}</p>
            )}

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500/30"
              />
              <label htmlFor="remember" className="text-sm text-slate-500">{language === "it" ? "Ricordami" : "Remember me"}</label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!email.includes("@") || !password || loading}
              className="w-full h-12 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{language === "it" ? "Accedi" : "Sign in"} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-800" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-slate-950 px-3 text-slate-400">{language === "it" ? "oppure" : "or"}</span></div>
          </div>

          {/* Google */}
          <button type="button" onClick={googleLogin}
            className="w-full h-12 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2.5"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {language === "it" ? "Continua con Google" : "Continue with Google"}
          </button>

          {/* Register link */}
          <p className="text-center text-sm text-slate-500 mt-6">
            {language === "it" ? "Non hai un account?" : "Don't have an account?"}{" "}
            <Link to="/onboarding" className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors" data-testid="register-link">
              {language === "it" ? "Registrati gratis" : "Sign up free"}
            </Link>
          </p>
        </div>
      </div>

      {/* RIGHT — VISUAL */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700" />
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white w-full">
          <div className="max-w-md space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-sm font-medium backdrop-blur-sm">
                <Sparkles className="w-4 h-4" /> SmartBooker
              </div>
              <h2 className="text-3xl font-bold leading-tight" key={headlineIdx}>
                {language === "it" ? headline.it : headline.en}
              </h2>
            </div>
            <div className="space-y-4">
              {[
                { icon: Calendar, text: language === "it" ? "Prenotazioni automatiche 24/7" : "24/7 auto bookings" },
                { icon: MessageSquare, text: language === "it" ? "WhatsApp automation integrata" : "Integrated WhatsApp automation" },
                { icon: Bell, text: language === "it" ? "Reminder anti no-show" : "Anti no-show reminders" },
                { icon: TrendingUp, text: language === "it" ? "Dashboard revenue e analytics" : "Revenue dashboard & analytics" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><f.icon className="w-4 h-4" /></div>
                  <span className="text-white/90 text-sm">{f.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 rounded-2xl bg-white/10 backdrop-blur-sm">
              <p className="text-white/90 text-sm italic leading-relaxed">
                {`"${language === "it" ? "Da quando uso SmartBooker, i no-show sono azzerati e ho recuperato 3 ore al giorno." : "Since using SmartBooker, no-shows are zero and I recovered 3 hours a day."}"`}
              </p>
              <p className="text-white/60 text-xs mt-3">Dott. Marco V. — Studio di Fisioterapia</p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          ACCOUNT SUSPENDED MODAL (trial expired)
      ============================================================ */}
      {suspendedInfo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-slate-950/80 backdrop-blur-sm"
          data-testid="account-suspended-modal"
          onClick={() => setSuspendedInfo(null)}
        >
          <div
            className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Banner */}
            <div className="relative px-7 py-6 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white overflow-hidden">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-start gap-4">
                <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-80">
                    {language === "it" ? "Account sospeso" : "Account suspended"}
                  </p>
                  <h2 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
                    {language === "it"
                      ? "Il tuo periodo di prova è terminato"
                      : "Your free trial has ended"}
                  </h2>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-7 py-6">
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                {language === "it" ? (
                  <>
                    Ciao{suspendedInfo.tenant_name ? <> <span className="font-semibold">{suspendedInfo.tenant_name}</span></> : ""}, il tuo trial gratuito di SmartBooker è terminato. <br />
                    Per continuare a usare la piattaforma puoi <strong>attivare l{`'`}abbonamento</strong> oppure contattarci per qualsiasi domanda.
                  </>
                ) : (
                  <>
                    Hi{suspendedInfo.tenant_name ? <> <span className="font-semibold">{suspendedInfo.tenant_name}</span></> : ""}, your free SmartBooker trial has ended.<br />
                    To keep using the platform you can <strong>activate your subscription</strong> or contact us with any question.
                  </>
                )}
              </p>

              <div className="mt-5 space-y-2.5">
                <Link
                  to="/pricing"
                  className="flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 font-semibold text-sm transition-all hover:translate-x-0.5"
                  data-testid="suspended-activate-cta"
                >
                  <span className="inline-flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {language === "it" ? "Attiva abbonamento" : "Activate subscription"}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href={`mailto:${suspendedInfo.contact_email || "info@smartbooker.it"}?subject=${encodeURIComponent(
                    language === "it"
                      ? "Riattivazione account SmartBooker"
                      : "Re-activate SmartBooker account"
                  )}`}
                  className="flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-white/15 hover:border-slate-400 dark:hover:border-white/25 text-slate-900 dark:text-white font-medium text-sm transition-colors"
                  data-testid="suspended-email-cta"
                >
                  <span className="inline-flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-500" />
                    {suspendedInfo.contact_email || "info@smartbooker.it"}
                  </span>
                  <ArrowRight className="w-4 h-4 opacity-50" />
                </a>

                <a
                  href={suspendedInfo.whatsapp_url || "https://wa.me/393770873260"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl border border-emerald-300/60 hover:border-emerald-400 hover:bg-emerald-50/40 dark:border-emerald-900/40 dark:hover:bg-emerald-950/20 text-slate-900 dark:text-white font-medium text-sm transition-colors"
                  data-testid="suspended-whatsapp-cta"
                >
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-emerald-500" />
                    {language === "it" ? "Scrivici su WhatsApp" : "Message us on WhatsApp"}
                    <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">
                      {suspendedInfo.whatsapp_number || "+39 377 087 3260"}
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 opacity-50" />
                </a>
              </div>

              <button
                onClick={() => setSuspendedInfo(null)}
                className="mt-5 w-full text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                data-testid="suspended-close-btn"
              >
                {language === "it" ? "Chiudi" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
